import { Box, Skeleton, Typography, Grid, Chip } from '@mui/material';
import {
  SwapHoriz as TransferIcon,
  Input as EntryIcon,
  DeleteOutline as DiscardIcon,
  Inventory as InventoryIcon,
  CallSplit as DekitIcon,
  CallMerge as KitIcon,
  SyncAlt as SwapIcon,
} from '@mui/icons-material';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget from './DashboardWidget';

const movementIcons: Record<string, React.ReactNode> = {
  Entry: <EntryIcon />,
  Transfer: <TransferIcon />,
  Inventory: <InventoryIcon />,
  Discard: <DiscardIcon />,
};

const convIcons: Record<string, React.ReactNode> = {
  KIT: <KitIcon />,
  'DE-KIT': <DekitIcon />,
  SWAP: <SwapIcon />,
};

const movementColors: Record<string, string> = {
  Entry: '#2e7d32',
  Transfer: '#0288d1',
  Inventory: '#7b1fa2',
  Discard: '#d32f2f',
};

const convColors: Record<string, string> = {
  KIT: '#00695c',
  'DE-KIT': '#ed6c02',
  SWAP: '#0288d1',
};

interface MiniCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}

const MiniCard = ({ icon, label, count, color }: MiniCardProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      bgcolor: `${color}08`,
      border: `1px solid ${color}20`,
    }}
  >
    <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {count}
      </Typography>
    </Box>
  </Box>
);

const MovementSummary = () => {
  const { movementCounts, conversionCounts, sectionLoading } = useDashboard();
  const { t } = useTranslation();

  const totalMoves = movementCounts.reduce((sum, m) => sum + m.count, 0);
  const totalConv = conversionCounts.reduce((sum, c) => sum + c.count, 0);

  if (sectionLoading.activity) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {[1, 2].map((i) => (
          <DashboardWidget key={i} sx={{ flex: 1 }} chartHeight={120}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[1, 2, 3].map((j) => (
                <Skeleton key={j} variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
              ))}
            </Box>
          </DashboardWidget>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
      <DashboardWidget
        sx={{ flex: 1 }}
        fitContent
        header={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.movements.title')}
            </Typography>
            <DashboardInfoTip helpKey="movements.title" />
            <Chip label={totalMoves} size="small" color="primary" variant="outlined" />
          </Box>
        }
      >
        <Grid container spacing={1.5}>
          {movementCounts.length > 0 ? (
            movementCounts.map((m) => (
              <Grid size={{ xs: 6 }} key={m.type}>
                <MiniCard
                  icon={movementIcons[m.type] || <TransferIcon />}
                  label={m.type}
                  count={m.count}
                  color={movementColors[m.type] || '#666'}
                />
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: 'text.secondary' }}>
                <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DashboardWidget>

      <DashboardWidget
        sx={{ flex: 1 }}
        fitContent
        header={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.conversions.title')}
            </Typography>
            <DashboardInfoTip helpKey="conversions.title" />
            <Chip label={totalConv} size="small" color="secondary" variant="outlined" />
          </Box>
        }
      >
        <Grid container spacing={1.5}>
          {conversionCounts.length > 0 ? (
            conversionCounts.map((c) => (
              <Grid size={{ xs: 6 }} key={c.type}>
                <MiniCard
                  icon={convIcons[c.type] || <SwapIcon />}
                  label={c.type}
                  count={c.count}
                  color={convColors[c.type] || '#666'}
                />
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: 'text.secondary' }}>
                <Typography variant="body2">{t('common:dashboard.noData')}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DashboardWidget>
    </Box>
  );
};

export default MovementSummary;